import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CookieOptions, CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, catchError, EMPTY, first, Observable } from 'rxjs';

import { convertToString } from '@VineyardLib/utils/typeConversion.util';
import { TokenRefreshStatus } from '@VineyardLibConstant/constant';

/**
 * OAuthランタイム・トークンのResponse(JSON Webトークン形式(JWT))
 * {@link https://docs.oracle.com/cd/E83857_01/paas/identity-cloud/rest-api/op-oauth2-v1-token-post.html Oracle}
 */
export interface Token {
  // アクセストークン
  access_token?: string;
  // アクセストークンの有効期限(単位：秒)
  expires_in?: number;
  // リフレッシュトークン
  refresh_token?: string;
  // アクセストークンタイプ
  token_type?: string;
}

/**
 * tokenを保持するcookie用interface
 *
 * @property tokenString、TokenのjsonString
 * @property tokenCreateDate、Tokenの作成日時
 */
interface TokenCookie {
  tokenJsonString: string;
  tokenCreateDate: Date;
}

const TIME_BEFORE_REFRESH_MSEC = 10000;
const SESSION_TIME_OUT_MSEC = 3600000;
const REFRESH_TOKEN_EXPIRES_MSEC = 7200000;
const COOKIE_EXPIRES_DAY = 7; // リフレッシュトークンの有効期限と同じ7日　TODO：リフレッシュトークンの有効期限は2時間になる想定

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private httpClient = inject(HttpClient);
  private cookieService = inject(CookieService);

  // クッキー設定
  private readonly cookieOptions: CookieOptions = {
    // set cookiePathで設定する
    path: '',
    expires: COOKIE_EXPIRES_DAY,
    secure: true,
    sameSite: 'Strict',
  };
  private readonly tokenCookieName: string = 'token';
  private readonly lastAccessTimeCookieName: string = 'lastAccessTime';

  private authenticated: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public refreshFailure$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  // リフレッシュ中かどうか
  public tokenRefreshStatus: BehaviorSubject<TokenRefreshStatus> =
    new BehaviorSubject<TokenRefreshStatus>(TokenRefreshStatus.Completed);

  /**
   * クッキーパスの設定
   */
  public set cookiePath(value: string) {
    this.cookieOptions.path = value;
  }

  /**
   *  認証のオブザーバー
   */
  public get authenticated$(): Observable<boolean> {
    return this.authenticated.asObservable();
  }

  /**
   * トークンのリフレッシュ状態を通知するオブザーバー
   */
  public get tokenRefreshStatus$(): Observable<TokenRefreshStatus> {
    return this.tokenRefreshStatus.asObservable();
  }

  /**
   * 認証の有無
   */
  public get isAuthorized(): boolean {
    return this.getToken() != null;
  }

  /**
   * CookieからOAuthランタイム・トークンの取得
   */
  public getToken(): Token | undefined {
    if (!this.cookieService.check(this.tokenCookieName)) {
      return;
    }

    const tokenCookie = this.cookieService.get(this.tokenCookieName);
    const parsedTokenCookie = JSON.parse(tokenCookie) as TokenCookie;
    const tokenJsonString = parsedTokenCookie?.tokenJsonString;

    this.markLoggedIn(); // リロード時に必要な認証処理を行う
    return JSON.parse(tokenJsonString) as Token;
  }

  /**
   * 認証(トークンの取得)
   * @param username ユーザ名
   * @param password パスワード
   * {@link https://docs.oracle.com/cd/E83857_01/paas/identity-cloud/rest-api/op-oauth2-v1-token-post.html Oracle}
   */
  public authenticate(username: string, password: string): void {
    const request = `grant_type=password&username=${username}&password=${password}&scope=urn:opc:idm:__myscopes__%20offline_access`;
    this.httpClient
      .post<Token>('/oauth2/v1/token', request)
      .subscribe((data) => {
        this.setTokenCookie(data);
        this.markLoggedIn();
      });
  }

  /**
   * リフレッシュトークン、クッキーの取消
   * {@link https://docs.oracle.com/cd/E83857_01/paas/identity-cloud/rest-api/api-oauth-runtime-revoke-refresh-token.html Oracle}
   */
  public revoke(): void {
    // 他のリクエストやタブ等で行われたログアウトによってCookieのTokenが削除されている場合を想定し、
    // tokenがあるときのみリフレッシュトークンの無効化を行う。
    if (this.getToken() != null) {
      const request = `token=${this.getToken()?.refresh_token}`;

      this.httpClient
        .post<Token>('/oauth2/v1/revoke', request)
        .pipe(first())
        .subscribe(() => {});
    }

    if (this.authenticated.getValue()) {
      this.authenticated.next(false);
    }
    this.deleteCookie();
  }

  /**
   * アクセストークンとリフレッシュトークンのリフレッシュ
   * {@link https://docs.oracle.com/cd/E83857_01/paas/identity-cloud/rest-api/op-oauth2-v1-token-post.html Oracle}
   */
  public refresh(): void {
    this.tokenRefreshStatus.next(TokenRefreshStatus.InProgress);

    const request = `grant_type=refresh_token&refresh_token=${this.getToken()?.refresh_token}&scope=urn:opc:idm:__myscopes__`;
    this.httpClient
      .post<Token>('/oauth2/v1/token', request)
      .pipe(
        catchError(() => {
          this.tokenRefreshStatus.next(TokenRefreshStatus.Error);
          return EMPTY;
        })
      )
      .subscribe((data) => {
        this.setTokenCookie(data);
        this.tokenRefreshStatus.next(TokenRefreshStatus.Completed);
      });
  }

  /**
   * セッション切れの状態か
   */
  public isExpiredSession(): boolean {
    // cookieにtokenが無い場合は、apiExecutionTimeが無い場合は、セッション切れとして返す
    if (
      !this.cookieService.check(this.tokenCookieName) ||
      !this.cookieService.check(this.lastAccessTimeCookieName)
    ) {
      return true;
    }

    const lastAccessTimeCookie = this.cookieService.get(
      this.lastAccessTimeCookieName
    );
    const elapsedTime = new Date().getTime() - Number(lastAccessTimeCookie);
    return elapsedTime >= SESSION_TIME_OUT_MSEC;
  }

  /**
   * アクセストークンが期限切れか
   */
  public isExpiredAccessToken(): boolean {
    // 有効期限（トークンの作成日時 + トークンの有効期限 - TIME_BEFORE_REFRESH_MSEC前）
    const expiryDateTime =
      this.getTokenCreateDateTime() +
      Number(this.getToken()?.expires_in) * 1000 -
      TIME_BEFORE_REFRESH_MSEC;

    return new Date().getTime() >= expiryDateTime;
  }

  /**
   * リフレッシュトークンが期限切れか
   * TODO：現時点ではリフレッシュトークンの有効期限は2時間ではないが、今後2時間になる予定なので2時間を設定
   */
  public isExpiredRefreshToken(): boolean {
    // cookieにtokenが無い場合、apiExecutionTimeが無い場合は、期限切れとして返す
    if (
      !this.cookieService.check(this.tokenCookieName) ||
      !this.cookieService.check(this.lastAccessTimeCookieName)
    ) {
      return true;
    }

    // 有効期限（トークンの作成日時 + REFRESH_TOKEN_EXPIRES_MSEC）
    const expiryDateTime =
      this.getTokenCreateDateTime() + REFRESH_TOKEN_EXPIRES_MSEC;
    return new Date().getTime() > expiryDateTime;
  }

  /**
   * ログイン時の認証処理
   */
  private markLoggedIn() {
    if (this.authenticated.getValue()) {
      // 既に認証済みの場合は、何も行わない
      return;
    }

    if (!this.cookieService.check(this.lastAccessTimeCookieName)) {
      // lastAccessTimeCookieが既に有る場合は、設定しない（リロード時は、設定しないように）
      this.setLastAccessTimeCookie(); // 最初のServerAPIを叩く前に、セッション確認のための初期値を設定
    }

    this.authenticated.next(true);
  }

  /**
   * CookieにAPI実行時刻を登録
   */
  public setLastAccessTimeCookie() {
    this.cookieService.set(
      this.lastAccessTimeCookieName,
      convertToString(new Date().getTime()),
      this.cookieOptions
    );
  }

  private setTokenCookie(value: Token | undefined): void {
    const tokenCookie: TokenCookie = {
      tokenJsonString: JSON.stringify(value),
      tokenCreateDate: new Date(),
    };
    // クッキーをセットする
    this.cookieService.set(
      this.tokenCookieName,
      JSON.stringify(tokenCookie),
      this.cookieOptions
    );
  }

  private deleteCookie(): void {
    this.cookieService.delete(this.tokenCookieName, this.cookieOptions.path);
    this.cookieService.delete(
      this.lastAccessTimeCookieName,
      this.cookieOptions.path
    );
  }

  private getTokenCreateDateTime(): number {
    const tokenCookie = this.cookieService.get(this.tokenCookieName);
    const parsedTokenCookie = JSON.parse(tokenCookie) as TokenCookie;
    return new Date(parsedTokenCookie.tokenCreateDate).getTime();
  }
}
